<script setup lang="ts">
import IconDropdownClose from '@patsnap-ui/icon/assets/solid/DropdownClose.svg';
import IconDropdownOpen from '@patsnap-ui/icon/assets/solid/DropdownOpen.svg';
import IconRegenerate from '@/presentation/assets/images/icons/icon-regenerate.svg';
import SvgIcon from '@telecom/shared/components/SvgIcon.vue';
import BadgeText from '@/presentation/components/badge-text/BadgeText.vue';
import { ref, computed, watch } from 'vue';
import { ElPopover } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { EmbodimentFeatureKeywordsService } from './service/embodiment-feature-keywords-service';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import type { SelectedClaimFeature, KeywordsItem, SelectedKeyword } from './types';

const props = defineProps<{
    modelValue?: SelectedKeyword;
    disabled?: boolean;
    claimFeature?: SelectedClaimFeature;
}>();

const emits = defineEmits<{
    (e: 'update:modelValue', value: SelectedKeyword): void;
}>();

const embodimentFeatureKeywordsService = injectService<EmbodimentFeatureKeywordsService>(EmbodimentFeatureKeywordsService);

const { t } = useI18n();

const isShowPopover = ref(false);

const isLoading = ref(false);

const isAlreadyInit = ref(false);

const keywordList = ref<KeywordsItem[]>([]);

const selectedValue = computed({
    get: () => props.modelValue || { type: '', value: '' },
    set: (value: SelectedKeyword) => emits('update:modelValue', value),
});

function handleSelect(value: string, type: string) {
    selectedValue.value = { type, value };
    isShowPopover.value = false;
}

async function getKeywordList() {
    isAlreadyInit.value = true;
    isLoading.value = true;

    keywordList.value = [];
    keywordList.value = await embodimentFeatureKeywordsService.fetchKeywords(props.claimFeature?.num || '', props.claimFeature?.feature || '');

    isLoading.value = false;
}

watch(() => isShowPopover.value, value => {
    if (value && !isAlreadyInit.value) {
        getKeywordList();
    }
});
</script>

<template>
    <ElPopover
        v-model:visible="isShowPopover"
        class="embodiment-keyword-selector"
        :trigger="'click'"
        :showArrow="false"
        :placement="'bottom-end'"
        :hideAfter="0"
        :width="400"
        :transition="'none'"
        :popperOptions="{ modifiers: [{ name: 'offset', options: { offset: [0, 4] } }] }"
        :disabled="disabled"
        popperClass="embodiment-keyword-selector__popover-wrapper"
    >
        <!-- 触发按钮区域 -->
        <template #reference>
            <div
                :class="{ '!border-green-500': isShowPopover, '!cursor-not-allowed': disabled }"
                class="embodiment-keyword-selector w-full py-1 px-3 pr-1 rounded bg-green-75 border border-green-75 flex justify-between items-center overflow-hidden cursor-pointer"
            >
                <!-- 选中的关键词或占位符 -->
                <span class="overflow-hidden text-ellipsis whitespace-nowrap flex-1">
                    <span v-if="modelValue?.value" class="text-black">
                        {{ modelValue.value }}
                    </span>
                    <span v-else class="text-gray-90">
                        {{ t('aiSpecification.embodiment.replacementTermPlaceholder') }}
                    </span>
                </span>
                <!-- 展开/收起图标 -->
                <SvgIcon
                    :icon="isShowPopover ? IconDropdownClose : IconDropdownOpen"
                    class="th-btn-icon h-6 w-6 fill-gray-450 flex-shrink-0"
                />
            </div>
        </template>

        <!-- 弹出层内容 -->
        <template #default>
            <div class="embodiment-keyword-selector__popover h-[200px] flex flex-col">
                <!-- 弹出层标题栏 -->
                <div class="embodiment-keyword-selector__popover__title flex flex-shrink-0 justify-between items-center">
                    <p class="text-gray-90 text-xs">
                        {{ t('aiSpecification.embodiment.adjustmentTerm') }}
                    </p>
                    <!-- 重新生成按钮 -->
                    <button
                        :disabled="isLoading"
                        class="th-btn th-btn-outline th-btn-sm pl-2"
                        @click="getKeywordList"
                    >
                        <SvgIcon
                            :icon="IconRegenerate"
                            class="th-btn-icon h-5 w-5 fill-gray-450"
                        />
                        <span>{{ t('aiDisclosureGlobal.globalBtn.regenerate') }}</span>
                    </button>
                </div>

                <!-- 关键词列表内容区 -->
                <div
                    v-if="!isLoading"
                    class="embodiment-keyword-selector__popover__content flex-1 mt-2 overflow-auto"
                >
                    <!-- 关键词分类列表 -->
                    <div
                        v-for="(item, index) in keywordList"
                        :key="index"
                        class="embodiment-keyword-selector__popover__content-item mt-2 first-of-type:mt-0"
                    >
                        <!-- 分类标题 -->
                        <p class="text-xs text-gray-450">
                            {{ t(`aiSpecification.embodiment.${item.type}`) }}
                        </p>
                        <!-- 关键词标签组 -->
                        <div class="mt-1">
                            <BadgeText
                                v-for="(keyword, keywordIndex) in item.value"
                                :key="keywordIndex"
                                :name="keyword"
                                :isSelected="modelValue?.value === keyword"
                                theme="green"
                                class="mr-1 mb-1"
                                @click="handleSelect(keyword, item.type)"
                            />
                        </div>
                    </div>
                </div>

                <!-- 加载中状态 -->
                <div
                    v-else
                    class="embodiment-keyword-selector__popover__loading mt-2"
                >
                    <!-- 加载中占位骨架屏 -->
                    <div>
                        <span class="embodiment-keyword-selector__popover__loading-item w-[80px]" />
                        <span class="embodiment-keyword-selector__popover__loading-item w-[120px]" />
                    </div>
                    <div class="mt-2">
                        <span class="embodiment-keyword-selector__popover__loading-item w-[120px]" />
                        <span class="embodiment-keyword-selector__popover__loading-item w-[80px]" />
                        <span class="embodiment-keyword-selector__popover__loading-item w-[80px]" />
                    </div>
                    <div class="mt-2">
                        <span class="embodiment-keyword-selector__popover__loading-item w-[80px]" />
                        <span class="embodiment-keyword-selector__popover__loading-item w-[120px]" />
                    </div>
                </div>
            </div>
        </template>
    </ElPopover>
</template>

<style lang="scss">
.embodiment-keyword-selector__popover__loading {
    .embodiment-keyword-selector__popover__loading-item {
        @apply h-[28px] rounded bg-gray-20 mr-1 inline-block;
    }
}
</style>
